'use client';

import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { MultiColumnSelector } from '../ColumnSelector';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { useSchema, useNodeSchema } from '../../../contexts/SchemaContext';
import { ConditionBuilder, type Condition } from '../ConditionBuilder';
import { getNodeDisplayLabel } from '../../../utils/nodeDisplayLabel';
import type { DynamicGroup } from '../../../types';

interface DropConfigProps {
  nodeId: string;
  config: {
    mode?: 'columns' | 'rows';
    columns?: string[];
    rowMode?: 'empty' | 'specific' | 'condition';
    rowNumbers?: number[];
    conditions?: Condition[];
  };
}

function expandToPhysical(
  names: string[],
  dynamicGroups: DynamicGroup[]
): Set<string> {
  const set = new Set<string>();
  for (const n of names) {
    const g = dynamicGroups.find(dg => dg.commonName === n);
    if (g) g.physicalColumns.forEach(p => set.add(p));
    else set.add(n);
  }
  return set;
}

export function DropColumnsConfig({ nodeId, config }: DropConfigProps) {
  const { t, i18n } = useTranslation();
  const { nodes, edges, updateNodeConfig } = useWorkflowStore();
  const { register } = useNodeSchema(nodeId);
  const { getNodeSchema } = useSchema();
  const inputColumns = useNodeInputColumns(nodeId);

  const [mode, setMode] = useState<'columns' | 'rows'>(config.mode || 'columns');
  const [columns, setColumns] = useState<string[]>(config.columns || []);
  const [rowMode, setRowMode] = useState<'empty' | 'specific' | 'condition'>(config.rowMode || 'empty');
  const [rowNumbers, setRowNumbers] = useState<string>(config.rowNumbers?.join(', ') || '');
  const [conditions, setConditions] = useState<Condition[]>(
    config.conditions || []
  );

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.mode) setMode(config.mode);
    if (config.columns) setColumns(config.columns);
    if (config.rowMode) setRowMode(config.rowMode);
    if (config.rowNumbers) setRowNumbers(config.rowNumbers.join(', '));
    if (config.conditions) setConditions(config.conditions);
  }, [config.mode, config.columns, config.rowMode, config.rowNumbers, config.conditions]);

  const upstreamNodeId = useMemo(
    () => edges.find(e => e.target === nodeId)?.source,
    [edges, nodeId]
  );
  const upstreamSchema = useMemo(
    () => (upstreamNodeId ? getNodeSchema(upstreamNodeId) : undefined),
    [upstreamNodeId, getNodeSchema]
  );
  const upstreamColumns = useMemo(
    () => (inputColumns ?? []).map(c => c.column),
    [inputColumns]
  );
  const dynamicGroups = useMemo(
    () => upstreamSchema?.dynamicGroups ?? [],
    [upstreamSchema]
  );

  useEffect(() => {
    const params: Record<string, unknown> = { mode };
    if (mode === 'columns') {
      params.columns = columns;
    } else {
      params.rowMode = rowMode;
      if (rowMode === 'specific') {
        params.rowNumbers = rowNumbers
          .split(',')
          .map(s => parseInt(s.trim(), 10))
          .filter(n => !isNaN(n) && n > 0);
      } else if (rowMode === 'condition') {
        params.conditions = conditions;
      }
    }
    updateNodeConfig(nodeId, { type: 'drop', params });

    if (mode === 'columns') {
      syncOutputSchema(columns);
    } else {
      syncOutputSchema([]);
    }
  }, [mode, columns, rowMode, rowNumbers, conditions]);

  const syncOutputSchema = (nextDropColumns: string[]) => {
    if (!upstreamColumns.length) return;
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    const dropSet = expandToPhysical(nextDropColumns, dynamicGroups);
    const remaining = upstreamColumns.filter((col) => {
      if (col.dynamicGroup) {
        const g = dynamicGroups.find((dg) => dg.commonName === col.name);
        if (!g) return true;
        return !g.physicalColumns.every((p) => dropSet.has(p));
      }
      return !dropSet.has(col.name);
    });
    if (!remaining.length) return;

    const remainingDynamicGroups: DynamicGroup[] = dynamicGroups
      .map(g => ({
        commonName: g.commonName,
        physicalColumns: g.physicalColumns.filter(p => !dropSet.has(p)),
      }))
      .filter(g => g.physicalColumns.length > 0);

    register({
      nodeType: node.type,
      nodeName: getNodeDisplayLabel(node.type, node.data.label, t, i18n),
      columns: remaining,
      dynamicGroups: remainingDynamicGroups.length > 0 ? remainingDynamicGroups : undefined,
    });
  };

  return (
    <div className="space-y-4">
      {/* 模式选择 */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">{t('nodes.config.drop.mode')}</label>
        <div className="flex gap-2">
          <button
            onClick={() => setMode('columns')}
            className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
              mode === 'columns'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'
            }`}
          >
            {t('nodes.config.drop.modeColumns')}
          </button>
          <button
            onClick={() => setMode('rows')}
            className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
              mode === 'rows'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'
            }`}
          >
            {t('nodes.config.drop.modeRows')}
          </button>
        </div>
      </div>

      {/* 删除列模式 */}
      {mode === 'columns' && (
        <div className="space-y-4">
          <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
            <p className="text-xs text-amber-700">{t('nodes.config.drop.columnsWarning')}</p>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-medium text-zinc-500">{t('nodes.config.drop.columnsToDelete')}</label>
            <MultiColumnSelector
              values={columns}
              onChange={(cols) => setColumns(cols)}
              columns={inputColumns ?? []}
            />
            <p className="text-xs text-zinc-400 mt-1">{t('nodes.config.drop.selectColumns')}</p>
          </div>
        </div>
      )}

      {/* 删除行模式 */}
      {mode === 'rows' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-zinc-500">{t('nodes.config.drop.rowDeleteMode')}</label>
            <select
              value={rowMode}
              onChange={(e) => setRowMode(e.target.value as 'empty' | 'specific' | 'condition')}
              className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            >
              <option value="empty">{t('nodes.config.drop.deleteEmptyRows')}</option>
              <option value="specific">{t('nodes.config.drop.deleteSpecificRows')}</option>
              <option value="condition">{t('nodes.config.drop.deleteByCondition')}</option>
            </select>
          </div>

          {rowMode === 'specific' && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-zinc-500">{t('nodes.config.drop.rowNumbers')}</label>
              <input
                type="text"
                value={rowNumbers}
                onChange={(e) => setRowNumbers(e.target.value)}
                placeholder={t('nodes.config.drop.rowNumbersPlaceholder')}
                className="w-full text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
              <p className="text-xs text-zinc-400">{t('nodes.config.drop.rowNumbersHint')}</p>
            </div>
          )}

          {rowMode === 'condition' && (
            <ConditionBuilder
              conditions={conditions}
              onChange={setConditions}
              columns={inputColumns}
              showLogic={false}
              showTimeGranularity={false}
              title={t('nodes.config.drop.conditions')}
            />
          )}
        </div>
      )}
    </div>
  );
}
